import React, { useState, useEffect } from "react";
import { ref, onValue } from "../../../Config/matuRtdb";
import { database } from "../../../Config/firebase";
import { Clock, DollarSign, ArrowUpRight, Check, UserPlus } from "lucide-react";

const ActivityFeed = ({ userId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;

    // Referencia al nodo de usuarios en Firebase
    const userActivityRef = ref(database, `usuarios/${userId}/activities/Recent Activity`);

    const unsubscribe = onValue(userActivityRef, (snapshot) => {
      const activityData = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          activityData.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });

        // Ordenar actividades por fecha y hora (más recientes primero)
        activityData.sort((a, b) => {
          const dateA = new Date(`${a.date} ${a.hour}`);
          const dateB = new Date(`${b.date} ${b.hour}`);
          return dateB - dateA;
        });

        // Limitar a las 5 actividades más recientes
        setActivities(activityData.slice(0, 5));
      } else {
        // Si no hay datos, mostramos array vacío
        setActivities([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Función para formatear la fecha
  const formatDate = (date, hour) => {
    if (!date || !hour) return "fecha desconocida";

    const [day, month, year] = date.split("/");
    const [hours, minutes] = hour.split(":");

    const activityDate = new Date(year, month - 1, day, hours, minutes);
    const now = new Date();
    const diffTime = Math.abs(now - activityDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffDays > 0) {
      return `hace ${diffDays} ${diffDays === 1 ? "día" : "días"}`;
    } else if (diffHours > 0) {
      return `hace ${diffHours} ${diffHours === 1 ? "hora" : "horas"}`;
    } else if (diffMinutes > 0) {
      return `hace ${diffMinutes} ${diffMinutes === 1 ? "minuto" : "minutos"}`;
    } else {
      return "justo ahora";
    }
  };

  // Función para obtener el icono según el tipo de actividad
  const getActivityIcon = (type) => {
    switch (type) {
      case "registro":
        return <UserPlus size={18} className="text-blue-500" />;
      case "comision":
        return <DollarSign size={18} className="text-green-500" />;
      case "conversion":
        return <Check size={18} className="text-purple-500" />;
      case "click":
        return <ArrowUpRight size={18} className="text-yellow-500" />;
      default:
        return <Clock size={18} className="text-gray-400" />;
    }
  };

  // Función para obtener el contenido de la actividad
  const getActivityContent = (activity) => {
    switch (activity.type) {
      case "registro":
        return (
          <div>
            <span className="font-medium text-white">Nuevo afiliado</span>
            <p className="text-gray-400 text-sm">
              {activity.details?.message ||
                "Se ha registrado un nuevo afiliado con tu código"}
            </p>
          </div>
        );
      case "comision":
        return (
          <div>
            <span className="font-medium text-white">Comisión recibida</span>
            <p className="text-gray-400 text-sm">
              {activity.details?.message ||
                "Se ha acreditado una comisión a tu cuenta"}
            </p>
          </div>
        );
      case "conversion":
        return (
          <div>
            <span className="font-medium text-white">
              Conversión completada
            </span>
            <p className="text-gray-400 text-sm">
              {activity.details?.message ||
                "Un usuario ha completado una conversión"}
            </p>
          </div>
        );
      case "click":
        return (
          <div>
            <span className="font-medium text-white">Click en tu enlace</span>
            <p className="text-gray-400 text-sm">
              {activity.details?.message ||
                "Alguien hizo click en tu enlace de afiliado"}
            </p>
          </div>
        );
      default:
        return (
          <div>
            <span className="font-medium text-white">Actividad registrada</span>
            <p className="text-gray-400 text-sm">
              {activity.details?.message ||
                "Se ha registrado una nueva actividad"}
            </p>
          </div>
        );
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6 shadow-lg h-full">
      <h3 className="text-xl font-semibold text-white mb-4">
        Actividad Reciente
      </h3>

      {loading ? (
        <div className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-gray-400 mt-2">Cargando actividades...</p>
        </div>
      ) : activities.length === 0 ? (
        <div className="py-8 text-center text-gray-400">
          <p>No hay actividad reciente.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities.map((activity) => (
            <div
              key={activity.id}
              className="flex items-start space-x-3 pb-4 border-b border-gray-700 last:border-b-0"
            >
              <div className="bg-gray-700 p-2 rounded-full">
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1">
                {getActivityContent(activity)}
                <div className="text-xs text-gray-500 mt-1">
                  {formatDate(activity.date, activity.hour)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 text-right">
        <button className="text-blue-400 hover:text-blue-300 text-sm font-medium">
          Ver todo el historial →
        </button>
      </div>
    </div>
  );
};

export default ActivityFeed;
